import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import clsx from "clsx";
import type { DocxPageSize, DocxViewMode } from "@nevisign/reader-core";
import {
  compensateScrollForZoom,
  getCanvasScroll,
  pinchDistance,
  pinchMidpoint,
} from "../lib/pinchZoom";

type Props = {
  html: string;
  showTrackChanges: boolean;
  showComments?: boolean;
  activeCommentId: string | null;
  /** When false, document is not contentEditable (avoids soft keyboard). */
  editable?: boolean;
  viewMode: DocxViewMode;
  /** User zoom on top of fit-width (1 = fit). */
  zoom: number;
  pageSize: DocxPageSize;
  onCommentClick?: (commentId: string) => void;
  /** Tap on track-change mark → author / type. */
  onRevisionTap?: (info: {
    type: "ins" | "del";
    author: string;
    date?: string;
  }) => void;
  containerRef?: RefObject<HTMLDivElement | null>;
  onPageCount?: (n: number) => void;
  onVisiblePage?: (page: number) => void;
};

const A4_FALLBACK: DocxPageSize = {
  widthTwip: 11906,
  heightTwip: 16838,
  widthPx: 11906 / 15,
  heightPx: 16838 / 15,
  marginTopPx: 96,
  marginRightPx: 96,
  marginBottomPx: 96,
  marginLeftPx: 96,
};

/**
 * Word viewer: continuous full-width sheet, or print-like pages.
 * Zoom = CSS `zoom` so layout size matches visuals → native overflow scroll pans.
 */
export function DocxViewer({
  html,
  showTrackChanges,
  showComments = true,
  activeCommentId,
  editable = true,
  viewMode,
  zoom,
  pageSize,
  onCommentClick,
  onRevisionTap,
  containerRef,
  onPageCount,
  onVisiblePage,
}: Props) {
  const innerRef = useRef<HTMLDivElement>(null);
  const ref = containerRef ?? innerRef;
  const shellRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const lastHtml = useRef<string>("");
  const onCommentClickRef = useRef(onCommentClick);
  onCommentClickRef.current = onCommentClick;
  const onRevisionTapRef = useRef(onRevisionTap);
  onRevisionTapRef.current = onRevisionTap;
  const onPageCountRef = useRef(onPageCount);
  onPageCountRef.current = onPageCount;
  const onVisiblePageRef = useRef(onVisiblePage);
  onVisiblePageRef.current = onVisiblePage;

  const [fitScale, setFitScale] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const geom = pageSize?.widthPx ? pageSize : A4_FALLBACK;
  const scale = Math.max(0.35, Math.min(3, fitScale * zoom));

  // Fit paginated pages to shell width; continuous stays fluid (fitScale = 1)
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;
    const measure = () => {
      const avail = Math.max(0, shell.clientWidth - 8);
      if (avail <= 0) return;
      if (viewMode === "paginated") {
        setFitScale(Math.max(0.35, Math.min(2.5, avail / geom.widthPx)));
      } else {
        setFitScale(1);
      }
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(shell);
    return () => ro.disconnect();
  }, [viewMode, geom.widthPx, html]);

  // Reset scroll + html cache when switching modes so continuous re-injects cleanly
  useEffect(() => {
    lastHtml.current = "";
    const scrollRoot =
      (shellRef.current?.closest(".canvas-scroll") as HTMLElement | null) ??
      null;
    scrollRoot?.scrollTo({ left: 0, top: 0, behavior: "auto" });
  }, [viewMode]);

  // Inject HTML into the editable root (continuous)
  useEffect(() => {
    if (viewMode !== "continuous") return;
    const el = ref.current;
    if (!el) return;
    const key = `c:${html}`;
    if (lastHtml.current === key && el.innerHTML.trim()) return;
    el.innerHTML = html;
    lastHtml.current = key;
  }, [html, ref, viewMode]);

  // Paginated: measure + pack into page frames
  useLayoutEffect(() => {
    if (viewMode !== "paginated") {
      setPageCount(1);
      onPageCountRef.current?.(1);
      return;
    }
    const measureHost = measureRef.current;
    const pagesHost = ref.current;
    if (!measureHost || !pagesHost) return;

    measureHost.innerHTML = html;
    lastHtml.current = `p:${html}`;

    const contentW = geom.widthPx - geom.marginLeftPx - geom.marginRightPx;
    const contentH = geom.heightPx - geom.marginTopPx - geom.marginBottomPx;

    measureHost.style.width = `${Math.max(120, contentW)}px`;
    measureHost.style.padding = "0";

    const source = measureHost.querySelector(".nr-docx") ?? measureHost;
    const blocks = Array.from(source.childNodes);

    pagesHost.innerHTML = "";
    pagesHost.className = clsx(
      "docx-pages",
      !showTrackChanges && "hide-tc",
      !showComments && "hide-comments"
    );

    let used = 0;
    let pages = 0;
    let pageInner: HTMLElement | null = null;

    const startPage = (): HTMLElement => {
      pages += 1;
      const pageEl = document.createElement("div");
      pageEl.className = "docx-page";
      pageEl.dataset.page = String(pages);
      pageEl.style.width = `${geom.widthPx}px`;
      pageEl.style.height = `${geom.heightPx}px`;
      pageEl.style.padding = `${geom.marginTopPx}px ${geom.marginRightPx}px ${geom.marginBottomPx}px ${geom.marginLeftPx}px`;

      const inner = document.createElement("div");
      inner.className = "docx-page-inner nr-docx";
      inner.setAttribute("dir", "auto");
      if (editable) {
        inner.contentEditable = "true";
        inner.spellcheck = false;
        inner.classList.add("docx-editable");
      }
      pageEl.appendChild(inner);
      pagesHost.appendChild(pageEl);
      used = 0;
      pageInner = inner;
      return inner;
    };

    pageInner = startPage();

    for (const node of blocks) {
      let inner = pageInner;
      if (!inner) break;
      const clone = node.cloneNode(true);
      inner.appendChild(clone);

      const h = inner.scrollHeight;
      if (h > contentH + 1 && inner.childNodes.length > 1) {
        inner.removeChild(clone);
        inner = startPage();
        inner.appendChild(clone);
        used = inner.scrollHeight;
      } else {
        used = h;
      }
      void used;
    }

    if (pages === 0) startPage();

    setPageCount(pages);
    onPageCountRef.current?.(pages);
  }, [
    html,
    viewMode,
    geom.widthPx,
    geom.heightPx,
    geom.marginTopPx,
    geom.marginRightPx,
    geom.marginBottomPx,
    geom.marginLeftPx,
    showTrackChanges,
    showComments,
    editable,
    ref,
  ]);

  // Highlight + scroll active comment
  useEffect(() => {
    if (!activeCommentId || !ref.current) return;
    const root = ref.current;
    const marks = root.querySelectorAll(
      `.nr-comment-range[data-comment-ids~="${activeCommentId}"]`
    );
    marks.forEach((el) => {
      (el as HTMLElement).style.outline = "2px solid var(--accent)";
    });
    const anchor = root.querySelector(`#nr-cmt-${activeCommentId}`);
    anchor?.scrollIntoView({ behavior: "smooth", block: "center" });
    return () => {
      marks.forEach((el) => {
        (el as HTMLElement).style.outline = "";
      });
    };
  }, [activeCommentId, html, ref, viewMode, pageCount]);

  // Comment + track-change click handlers
  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const openFromMark = (mark: HTMLElement, e: Event) => {
      const ids =
        mark.getAttribute("data-comment-ids") ||
        mark.getAttribute("data-comment-id");
      if (!ids?.trim() || !onCommentClickRef.current) return false;
      e.preventDefault();
      e.stopPropagation();
      const sel = window.getSelection();
      sel?.removeAllRanges();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      root.blur();
      onCommentClickRef.current(ids.trim());
      return true;
    };

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const rev = target.closest(".nr-ins, .nr-del") as HTMLElement | null;
      if (rev && showTrackChanges && onRevisionTapRef.current) {
        const type = rev.classList.contains("nr-del") ? "del" : "ins";
        const author = rev.getAttribute("data-author") || "ناشناس";
        const date = rev.getAttribute("data-date") || undefined;
        e.preventDefault();
        e.stopPropagation();
        onRevisionTapRef.current({ type, author, date });
        return;
      }

      if (!onCommentClickRef.current) return;
      const mark = target.closest(
        ".nr-comment-range, .nr-comment-anchor"
      ) as HTMLElement | null;
      if (!mark) return;
      openFromMark(mark, e);
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(".nr-ins, .nr-del")) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      const mark = target.closest(
        ".nr-comment-range, .nr-comment-anchor"
      ) as HTMLElement | null;
      if (!mark) return;
      e.preventDefault();
      e.stopPropagation();
    };

    root.addEventListener("pointerdown", onPointerDown, true);
    root.addEventListener("click", onClick, true);
    return () => {
      root.removeEventListener("pointerdown", onPointerDown, true);
      root.removeEventListener("click", onClick, true);
    };
  }, [onCommentClick, onRevisionTap, ref, html, viewMode, pageCount, showTrackChanges]);

  useEffect(() => {
    if (editable) return;
    const el = ref.current;
    el?.blur();
    el?.querySelectorAll('[contenteditable="true"]').forEach((n) => {
      (n as HTMLElement).blur();
    });
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    window.getSelection()?.removeAllRanges();
  }, [editable, ref]);

  // Visible page while scrolling (paginated)
  useEffect(() => {
    if (viewMode !== "paginated" || pageCount < 1) return;
    const root = ref.current;
    if (!root) return;
    const ratios = new Map<number, number>();
    const scrollRoot = shellRef.current?.closest(".canvas-scroll") ?? null;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const n = Number((entry.target as HTMLElement).dataset.page);
          if (!Number.isFinite(n)) continue;
          ratios.set(n, entry.intersectionRatio);
        }
        let best = 1;
        let bestRatio = 0;
        for (const [n, r] of ratios) {
          if (r > bestRatio) {
            bestRatio = r;
            best = n;
          }
        }
        if (bestRatio > 0) onVisiblePageRef.current?.(best);
      },
      { root: scrollRoot, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    root.querySelectorAll("[data-page]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [viewMode, pageCount, scale, ref]);

  /** Continuous: full-width sheet; CSS zoom grows layout for overflow pan. */
  const continuousStyle: CSSProperties =
    viewMode === "continuous"
      ? ({
          zoom: scale,
          width: "100%",
          maxWidth: "100%",
          marginInline: "0",
        } as CSSProperties)
      : {};

  /**
   * Paginated: CSS `zoom` (not transform) so layout metrics match visual size.
   */
  const pagesScaleStyle: CSSProperties =
    viewMode === "paginated"
      ? ({
          zoom: scale,
          width: `${geom.widthPx}px`,
          marginInline: "auto",
        } as CSSProperties)
      : {};

  return (
    <div
      ref={shellRef}
      className={clsx(
        "docx-shell",
        viewMode === "paginated" ? "docx-shell-paginated" : "docx-shell-continuous"
      )}
    >
      <div ref={measureRef} className="docx-measure" aria-hidden />

      {viewMode === "continuous" ? (
        <div className="docx-continuous-wrap">
          <div
            ref={ref}
            className={clsx(
              "docx-sheet",
              !showTrackChanges && "hide-tc",
              !showComments && "hide-comments",
              editable && "docx-editable"
            )}
            style={continuousStyle}
            contentEditable={editable}
            suppressContentEditableWarning
            spellCheck={false}
            inputMode={editable ? "text" : "none"}
          />
        </div>
      ) : (
        <div className="docx-pages-wrap" dir="ltr">
          <div ref={ref} className="docx-pages" style={pagesScaleStyle} />
        </div>
      )}
    </div>
  );
}

/**
 * Photo-app pinch-zoom: distance → scale, focal point under fingers stays fixed
 * via scroll compensation. Pan remains native overflow scroll.
 */
export function usePinchZoom(
  enabled: boolean,
  zoom: number,
  setZoom: (z: number | ((prev: number) => number)) => void,
  min = 0.5,
  max = 3
) {
  const startDist = useRef(0);
  const startZoom = useRef(1);
  const lastZoom = useRef(1);
  const pinchActive = useRef(false);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const rafRef = useRef(0);
  const pendingZoom = useRef<number | null>(null);
  const pendingMid = useRef<{ x: number; y: number } | null>(null);
  const pendingEl = useRef<HTMLElement | null>(null);

  const clamp = useCallback(
    (v: number) => Math.max(min, Math.min(max, v)),
    [min, max]
  );

  const flushZoom = useCallback(
    (el: HTMLElement, midX: number, midY: number, next: number) => {
      const from = lastZoom.current;
      const to = clamp(next);
      if (Math.abs(to - from) < 0.0005) return;
      compensateScrollForZoom(el, midX, midY, from, to);
      lastZoom.current = to;
      setZoom(to);
    },
    [clamp, setZoom]
  );

  const onTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled || e.touches.length !== 2) return;
      const [a, b] = [e.touches[0]!, e.touches[1]!];
      startDist.current = pinchDistance(a, b);
      startZoom.current = zoomRef.current;
      lastZoom.current = zoomRef.current;
      pinchActive.current = true;
      document.documentElement.classList.add("nr-pinching");
    },
    [enabled]
  );

  const onTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!enabled || e.touches.length !== 2 || startDist.current <= 0) return;
      e.preventDefault();
      const el = e.currentTarget as HTMLElement;
      const [a, b] = [e.touches[0]!, e.touches[1]!];
      const dist = pinchDistance(a, b);
      if (dist < 1) return;
      const ratio = dist / startDist.current;
      const next = clamp(startZoom.current * ratio);
      const mid = pinchMidpoint(a, b);
      pendingZoom.current = next;
      pendingMid.current = mid;
      pendingEl.current = el;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        const z = pendingZoom.current;
        const m = pendingMid.current;
        const target = pendingEl.current;
        pendingZoom.current = null;
        if (z == null || m == null || !target || !pinchActive.current) return;
        flushZoom(target, m.x, m.y, z);
      });
    },
    [enabled, clamp, flushZoom]
  );

  const onTouchEnd = useCallback((e: TouchEvent) => {
    if (e.touches.length >= 2) return;
    startDist.current = 0;
    pinchActive.current = false;
    document.documentElement.classList.remove("nr-pinching");
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    pendingZoom.current = null;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const el = getCanvasScroll();
    if (!el) return;
    el.addEventListener("touchstart", onTouchStart as EventListener, {
      passive: true,
    });
    el.addEventListener("touchmove", onTouchMove as EventListener, {
      passive: false,
    });
    el.addEventListener("touchend", onTouchEnd as EventListener);
    el.addEventListener("touchcancel", onTouchEnd as EventListener);
    return () => {
      el.removeEventListener("touchstart", onTouchStart as EventListener);
      el.removeEventListener("touchmove", onTouchMove as EventListener);
      el.removeEventListener("touchend", onTouchEnd as EventListener);
      el.removeEventListener("touchcancel", onTouchEnd as EventListener);
      document.documentElement.classList.remove("nr-pinching");
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, onTouchStart, onTouchMove, onTouchEnd]);
}
